import { z } from 'zod';
import { requirementKeySchema } from './requirements.js';
import { isSegregatedCategory, fileCategorySchema } from '../files/categories.js';
/**
 * Requirement <-> StepDefinition. ONE reference, in ONE direction (ADR-006 §7).
 *
 *   RequirementRule = WHAT must be true. Owns the key, label, kind, expiry, phase,
 *                     blocking, visibility, trigger, satisfaction, citations.
 *   StepDefinition  = HOW a person makes it true. Owns the step type, config, controlled
 *                     text, presentation, ops, content hash, versions.
 *
 * The StepDefinition points AT the requirement. The requirement holds no step ids at all,
 * because a back-pointer is a second copy of the same edge and the two would disagree the
 * first time a definition was retired. "Which steps satisfy X" is a derived index,
 * consistent with the derived-state-only rule.
 */
/**
 * Added to the StepDefinition HEAD, never to its versioned `content`. Retargeting a step
 * at a different requirement is an operational change, not a change to what the person
 * read, so it must not move the content hash.
 */
export const stepDefinitionRequirementLinkSchema = z.object({
    satisfies: z.array(requirementKeySchema).default([]),
    /** `completes` closes the requirement on approval; `contributes` adds to an accumulator. */
    contribution: z.enum(['completes', 'contributes']).default('completes'),
});
/**
 * The one place the two systems could drift.
 *
 * `document-upload` config already carries a `requirementKey`, because `onApprove`
 * receives only the config and must name the key it is minting a credential for. That
 * field is the CREDENTIAL's key, not a second declaration of the link — so it is CHECKED
 * against the envelope rather than trusted.
 */
const credentialKeyCarryingConfigSchema = z
    .object({ requirementKey: z.string().min(1).nullable().optional() })
    .passthrough();
export function checkRequirementLink(satisfies, config) {
    const parsed = credentialKeyCarryingConfigSchema.safeParse(config);
    if (!parsed.success)
        return { ok: true };
    const configKey = parsed.data.requirementKey;
    if (configKey === undefined || configKey === null)
        return { ok: true };
    if (satisfies.includes(configKey))
        return { ok: true };
    return {
        ok: false,
        reason: `step config mints credential "${configKey}" but the definition does not declare it in satisfies`,
    };
}
/**
 * A definition may not file a segregated requirement's evidence in an ordinary library.
 *
 * The I-9 and every employee health record must land in a folder separate from the
 * personnel file, with tighter permissions (HR 4.8.1; catalog §3.1-§3.2). That was
 * previously a boolean somebody set by hand next to a free-text destination, so the two
 * could disagree — and did. Now the requirement's own target decides, and a definition
 * whose config would file the evidence somewhere else is rejected before it is stored.
 *
 * Pure, so the definition editor and the seeder both refuse the same thing.
 */
export function checkFileCategory(satisfies, config, targetFor) {
    const parsed = z.object({ target: fileCategorySchema.optional() }).passthrough().safeParse(config);
    if (!parsed.success)
        return { ok: true };
    const configTarget = parsed.data.target;
    // A step that files nothing (an acknowledgment, an info task) has no target to check.
    if (configTarget === undefined)
        return { ok: true };
    for (const key of satisfies) {
        const required = targetFor(key);
        if (required === undefined)
            continue;
        if (isSegregatedCategory(required) && configTarget !== required) {
            return {
                ok: false,
                reason: `"${key}" must be filed in ${required} — it is a segregated record — but this ` +
                    `definition files to ${configTarget}`,
            };
        }
    }
    return { ok: true };
}
/** Derived, never stored: the reverse edge, rebuilt whenever definitions are loaded. */
export function indexBySatisfies(definitions) {
    const index = new Map();
    for (const definition of definitions) {
        if (definition.retiredAt !== null)
            continue;
        for (const key of definition.satisfies) {
            const bucket = index.get(key);
            if (bucket)
                bucket.push(definition);
            else
                index.set(key, [definition]);
        }
    }
    return index;
}
/**
 * A requirement with no satisfying definition is legal — an `external-check` HR performs
 * and records directly. But a requirement whose satisfaction is step-based and which no
 * definition claims is a seeding bug, so the seeder fails on it rather than shipping a
 * checklist row nobody can ever close.
 */
export function findUnsatisfiableRequirements(stepBackedKeys, index) {
    return stepBackedKeys.filter((key) => (index.get(key) ?? []).length === 0);
}
//# sourceMappingURL=link.js.map